import { useQuery } from '@tanstack/react-query'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ArrowUpIcon, ArrowDownIcon, CurrencyEuroIcon, ClipboardDocumentListIcon, ServerIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import api from '../services/api'

interface DashboardStats {
  projects: { total: number; active: number; completed: number }
  clients: { total: number; active: number }
  devices: { total: number; online: number; offline: number }
  revenue: { total: number; pending: number; paid: number }
}

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/dashboard/stats')
      return response.data
    }
  })

  const mockRevenueData = [
    { month: 'Jan', revenue: 45000 },
    { month: 'Feb', revenue: 52000 },
    { month: 'Mar', revenue: 48000 },
    { month: 'Apr', revenue: 61000 },
    { month: 'May', revenue: 55000 },
    { month: 'Jun', revenue: 67000 },
  ]

  const mockProjectData = [
    { status: 'Draft', count: 5 },
    { status: 'In Progress', count: 12 },
    { status: 'Completed', count: 28 },
    { status: 'On Hold', count: 3 },
  ]

  const statCards = [
    {
      title: 'Total Projects',
      value: stats?.projects.total || 0,
      change: '+12%',
      trend: 'up',
      icon: ClipboardDocumentListIcon,
      color: 'bg-blue-500'
    },
    {
      title: 'Active Clients',
      value: stats?.clients.active || 0,
      change: '+5%',
      trend: 'up',
      icon: CurrencyEuroIcon,
      color: 'bg-green-500'
    },
    {
      title: 'Online Devices',
      value: stats?.devices.online || 0,
      change: '-2%',
      trend: 'down',
      icon: ServerIcon,
      color: 'bg-purple-500'
    },
    {
      title: 'Pending Issues',
      value: 8,
      change: '-15%',
      trend: 'down',
      icon: ExclamationTriangleIcon,
      color: 'bg-yellow-500'
    },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your projects and system status
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div key={stat.title} className="card overflow-hidden">
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md ${stat.color} p-3`}>
                  <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{stat.title}</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">{stat.value}</div>
                      <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                        stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.trend === 'up' ? (
                          <ArrowUpIcon className="h-4 w-4 flex-shrink-0 self-center" />
                        ) : (
                          <ArrowDownIcon className="h-4 w-4 flex-shrink-0 self-center" />
                        )}
                        <span className="ml-1">{stat.change}</span>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Revenue Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={mockRevenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="#93c5fd" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Project Status Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Project Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockProjectData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="flow-root">
            <ul className="-mb-8">
              {[
                { id: 1, type: 'project', action: 'New project created', project: 'OpenTCO Migration', time: '2 hours ago' },
                { id: 2, type: 'device', action: 'Device went offline', device: 'Server-01', time: '4 hours ago' },
                { id: 3, type: 'invoice', action: 'Invoice paid', invoice: 'INV-2024-001', time: '6 hours ago' },
                { id: 4, type: 'event', action: 'Maintenance completed', project: 'VitiPred System', time: '1 day ago' },
              ].map((activity, activityIdx) => (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {activityIdx !== 3 ? (
                      <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                          activity.type === 'project' ? 'bg-blue-500' :
                          activity.type === 'device' ? 'bg-red-500' :
                          activity.type === 'invoice' ? 'bg-green-500' :
                          'bg-yellow-500'
                        }`}>
                          <div className="h-2.5 w-2.5 rounded-full bg-white" />
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                        <div>
                          <p className="text-sm text-gray-500">
                            {activity.action}{' '}
                            <span className="font-medium text-gray-900">
                              {activity.project || activity.device || activity.invoice}
                            </span>
                          </p>
                        </div>
                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                          <time>{activity.time}</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}